<script>
    import API from "../../../API";

    import DialogBody from "../../dialog/components/dialog-body.vue";
    import DialogBase from "../../dialog/components/dialog-base.vue";
    import TextField from "../../common/text-field.vue";
    import DialogHeader from "../../dialog/components/dialog-header.vue";
    import PropertyTable from "../../common/property-table.vue";
    import Loading from "../../common/loading.vue";
    import DialogActionbar from "../../dialog/components/dialog-actionbar.vue";
    import toaster from "../../common/toaster";
    import HeaderCheckbox from "../../content/common/PrimeVue/HeaderCheckbox.vue";
    import DialogButton from "../../dialog/components/dialog-button.vue";

    export default {
        name: "system-message-dialog",
        components: {
            DialogButton,
            HeaderCheckbox,
            DialogActionbar,
            Loading,
            PropertyTable,
            DialogHeader,
            TextField,
            DialogBase,
            DialogBody
        },
        props: {
            authSuperadmin: String
        },
        data() {
            return {
                showDialog: false,
                processing: false,
                invalid: true,

                id: undefined,
                name: '',
                message: {},
                validFrom: '',
                validTo: '',
                active: true,
                addToNewProject: false,
                locked: false,
                displayedLanguage: Laravel.activeLanguage,

                downDateTime: undefined,

                allProjects: [],
            }
        },
        computed: {
            translations() {
                return {
                    privilegeName: Lang.langGet('System message name'),
                    addSystemMessage: Lang.langGet('Add new system message'),
                    editSystemMessage: Lang.langGet('Edit system message'),
                    name: Lang.langGet('Name'),
                    systemMessage: Lang.langGet('System Message'),
                    addedSuccessfully: Lang.langGet('System message :name added successfully', {name: this.name}),
                    editedSuccessfully: Lang.langGet('System message :name edited successfully', {name: this.name}),
                    validFrom: Lang.langGet('Valid from'),
                    validTo: Lang.langGet('Valid to'),
                    active: Lang.langGet('Active'),
                    inactive: Lang.langGet('Inactive'),
                    locked: Lang.langGet('Only super admin can edit'),
                    unlocked: Lang.langGet('Everyone can edit'),
                    addToNewProject: Lang.langGet('Add to future projects'),
                    dontAddToNewProject: Lang.langGet('Don\'t add to new Projects'),
                    projects: Lang.langGet('Projects'),
                    reactivate: Lang.langGet("Revoke OKs"),
                    reactivateUser: Lang.langGet("Revoke OKs (User)"),
                    specialDate: Lang.langGet("Date") + ' ' + Lang.langGet("and") + ' ' + Lang.langGet("Time"),
                    atLeastOneProject: Lang.langGet(`At least one project has to be selected!`)
                }
            },
            languages() {
                return Object.keys(Laravel.languages)
                    .map(key => ({value: key, text: Laravel.languages[key]}));
            },
            isImmutable() {
                if (!this.id) return false;
                // messages with id 20 or less are system messages made by us
                return this.id <= 20;
            },
            isServerUpdateWarning() {
                return this.id === 4;
            },
            errorProjectMessage() {
                if (!this.allProjects.length) return;

                const atLeastOneSelected = this.allProjects.some(project => project.selected);
                if (atLeastOneSelected) {
                    return null;
                }
                return this.translations.atLeastOneProject;
            }
        },
        watch: {
            name(newName) {
                const atLeastOneSelected = this.hasAtLeastOneProjectSelected();
                const noMessages = this.hasNoMessages();
                this.invalid = !newName || noMessages || !atLeastOneSelected;
            },
            allProjects(newProjects) {
                const atLeastOneSelected = this.hasAtLeastOneProjectSelected(newProjects);
                const noMessages = this.hasNoMessages();
                this.invalid = noMessages || !this.name || !atLeastOneSelected;
            }
        },
        methods: {
            hasNoMessages(message = this.message) {
                return _.isEmpty(message);
            },
            hasAtLeastOneProjectSelected(projects = this.allProjects) {
                return projects.some(project => project.selected);
            },
            show() { this.showDialog = true; },
            hide() { this.showDialog = false; },
            refreshList() {
                EaseEventBus.fire('reloadSystemMessageTable');
            },
            handleClose() { this.hide(); },
            handleChange(property, value) {
                this[property] = value;
            },
            handleMessageChange(value) {
                this.message[this.displayedLanguage] = value;

                if (!this.message[this.displayedLanguage]) {
                    delete this.message[this.displayedLanguage];
                }

                const atLeastOneSelected = this.hasAtLeastOneProjectSelected();
                const noMessages = this.hasNoMessages(value);
                this.invalid = noMessages || !this.name || !atLeastOneSelected;
            },
            handleSelectProject(newProject) {
                const index = this.allProjects.findIndex(project => project.value === newProject.value);
                if (index >= 0) {
                    this.allProjects.splice(index, 1, {...newProject, selected: !newProject.selected});
                }
            },
            handleSelectAllProjects(deselectAll) {
                if (deselectAll) {
                    this.allProjects = this.allProjects.map(project => ({...project, selected: false}));
                } else {
                    this.allProjects = this.allProjects.map(project => ({...project, selected: true}));
                }
            },
            stringRule(property = 'Name') {
                const name = Lang.langGet(property);
                const mustEnter = Lang.langGet('You must enter a ');

                if (property === 'Name') {
                    return [
                        function (value) {
                            if (value) {
                                return true;
                            }

                            return mustEnter + name;
                        }
                    ]
                }

                return [
                    () => {
                        if (this.hasNoMessages()) {
                            return mustEnter + name;
                        }
                        return true;
                    }
                ]
            },
            async reactivate(userOnly) {
                this.processing = true;
                const url = 'system-messages/clear-confirms';
                const api = new API(url);

                const result = await api.post({id: this.id, userOnly});
                this.processing = false;

                if (result.errored) {
                    this.errored = true;
                } else {
                    this.errored = false;
                    toaster.success(Lang.langGet("OKs revoked successfully"));
                }
            },
            async handleSave() {
                this.processing = true;
                const url = this.id ? 'system-messages/edit' : 'system-messages/add';
                const api = new API(url);

                const projects = this.allProjects.filter(project => project.selected).map(project => project.value);

                const data = {
                    name: this.name,
                    message: this.message,
                    validFrom: this.validFrom,
                    validTo: this.validTo,
                    active: this.active ? 1 : 0,
                    addToNewProject: this.addToNewProject ? 1 : 0,
                    locked: this.locked ? 1 : 0,
                    projects
                };

                if (this.isServerUpdateWarning) {
                    data.downDateTime = this.downDateTime;
                }

                if (this.id) {
                    data.id = this.id;
                }

                const result = await api.post(data);
                this.processing = false;

                if (result.errored) {
                    this.errored = true;
                } else {
                    this.errored = false;

                    if (this.id) {
                        toaster.success(this.translations.editedSuccessfully);
                    } else {
                        toaster.success(this.translations.addedSuccessfully);
                    }
                    this.refreshList();
                    this.hide();
                }
            },
            clear() {
                this.name = undefined;
                this.message = {};
                this.validFrom = undefined;
                this.validTo = undefined;
                this.active = true;
                this.addToNewProject = false;
                this.locked = false;
                this.displayedLanguage = Laravel.activeLanguage;
                this.allProjects = [];
            },
            async load() {
                this.processing = true;
                const url = 'system-messages/edit-entry';
                const api = new API(url);

                const result = await api.post({id: this.id});
                this.processing = false;

                // console.log('Loaded from API', result);

                if (result.errored) {
                    this.errored = true;
                    return;
                }

                this.errored = false;

                const {systemMessage, projectFilterMapping} = result;

                this.allProjects = projectFilterMapping.map(arr => ({value: arr[0], text: arr[1], selected: false}));

                if (!this.id) {
                    const activeProject = this.allProjects.find(project => project.value === Laravel.activeProject);
                    activeProject.selected = true;
                    return;
                }

                if (!systemMessage) return;

                this.allProjects.forEach(project => {
                    project.selected = systemMessage.projects.some(p => p.id === project.value);
                })

                this.name = systemMessage.name;
                this.message = systemMessage.message;
                this.validFrom = systemMessage.validFrom;
                this.validTo = systemMessage.validTo;
                this.active = systemMessage.active === 1;
                this.addToNewProject = systemMessage.addToNewProject === 1;
                this.locked = systemMessage.locked === 1;
                this.displayedLanguage = systemMessage.displayedLanguage;

                this.downDateTime = this.message.datetime;
            },
            calculateTitleAndButtonHeight() {
                const heightDialogHeader = document.getElementById('privilegegroup-dialog-header')?.offsetHeight;
                const heightDialogActionbar = document.getElementById('privilegegroup-dialog-actionbar')?.offsetHeight;
                return heightDialogHeader + heightDialogActionbar ? heightDialogHeader + heightDialogActionbar : 124;
            }
        },
        mounted: function () {
            EaseEventBus.listen('systemMessageAdd', () => {
                this.clear();
                this.id = null;
                this.show();
                this.load();
            });

            EaseEventBus.listen('systemMessageEdit', params => {
                this.clear();
                this.id = params.id;
                this.show();
                this.load();
            });
        }
    }
</script>

<template>
    <dialog-base
        :maxWidth="980"
        @close="handleClose"
        :showDialog="showDialog"
    >
        <dialog-header
            @close="handleClose"
            :title="id ? translations.editSystemMessage : translations.addSystemMessage"
        />
        <loading :loading="processing"/>
        <dialog-body v-if="showDialog" :style="'max-height: calc(90vh - ' + calculateTitleAndButtonHeight() + 'px); overflow: auto;'">
            <div style="max-height: 70vh">
                <div class="d-flex flex-row">
                    <div style="width: 30%;" class="d-flex flex-column">
                        <text-field
                            class="pa-4"
                            :disabled="isImmutable"
                            :label="translations.name"
                            :value="name"
                            :rules="stringRule()"
                            autofocus
                            @input="val => handleChange('name', val)"
                        />
                        <v-select-custom
                            class="pa-4"
                            :value="displayedLanguage"
                            @input="val => handleChange('displayedLanguage', val)"
                            :items="languages"
                            label="Language"
                            popup-used-in-modal
                        />
                        <div class="pa-4">
                            <datepicker
                                :label="translations.validFrom"
                                :value="validFrom"
                                hide-details
                                popup-used-in-modal
                                @input="val => handleChange('validFrom', val)"
                            />
                        </div>
                        <div class="pa-4">
                            <datepicker
                                :label="translations.validTo"
                                :value="validTo"
                                hide-details
                                popup-used-in-modal
                                @input="val => handleChange('validTo', val)"
                            />
                        </div>
                    </div>
                    <div class="d-flex flex-column flex-grow-1">
                        <text-field v-if="isServerUpdateWarning"
                            class="pa-4"
                            :label="translations.specialDate"
                            :value="downDateTime"
                            @input="val => handleChange('downDateTime', val)"
                        />
                        <text-field
                            class="pa-4"
                            :disabled="isImmutable"
                            :rules="stringRule('Message')"
                            textarea
                            noResize
                            autoGrow
                            :label="translations.systemMessage"
                            :value="message[displayedLanguage]"
                            @input="val => handleMessageChange(val)"
                        />
                    </div>
                </div>
                <div class="d-flex flex-row">
                    <div style="width: 30%;" class="d-flex flex-column">
                        <div class="d-flex flex-row pa-4 justify-space-between">
                            <div>{{translations.active + ':'}}</div>
                            <HeaderCheckbox
                                :selectMessage="translations.active"
                                :deselectMessage="translations.inactive"
                                :checked="active"
                                small
                                @change="({checked}) => handleChange('active', checked)"
                            />
                        </div>
                        <div v-if="authSuperadmin" class="d-flex flex-row pa-4 justify-space-between">
                            <div>{{translations.addToNewProject + ':'}}</div>
                            <HeaderCheckbox
                                :selectMessage="translations.addToNewProject"
                                :deselectMessage="translations.dontAddToNewProject"
                                :checked="addToNewProject"
                                small
                                @change="({checked}) => handleChange('addToNewProject', checked)"
                            />
                        </div>
                        <div v-if="authSuperadmin" class="d-flex flex-row pa-4 justify-space-between">
                            <div>{{translations.locked + ':'}}</div>
                            <HeaderCheckbox
                                :selectMessage="translations.locked"
                                :deselectMessage="translations.unlocked"
                                :checked="locked"
                                small
                                @change="({checked}) => handleChange('locked', checked)"
                            />
                        </div>
                    </div>
                    <div class="d-flex flex-column flex-grow-1">
                        <div class="pa-4">
                            <property-table
                                :errorMessage="errorProjectMessage"
                                :items="allProjects"
                                :providedMaxHeight="30"
                                :propertyName="translations.projects"
                                @input="value => handleSelectProject(value)"
                                @selectAll="value => handleSelectAllProjects(value)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </dialog-body>
        <dialog-actionbar
            :disableSave="invalid"
            @close="handleClose"
            @save="handleSave"
        >
            <div v-if="id" class="d-flex flex-row">
                <dialog-button v-if="authSuperadmin" color="secondary" class="mr-1" @click="reactivate">
                    {{translations.reactivate}}
                </dialog-button>
                <dialog-button v-if="authSuperadmin" color="secondary" @click="reactivate">
                    {{translations.reactivateUser}}
                </dialog-button>
            </div>
        </dialog-actionbar>
    </dialog-base>
</template>
